// ============================================================
// HALLUCINATION - an intensity, not a table (v8.357.0).
//
// The user's ruling: no table of specific visions. A hallucination is
// an INTENSITY from 1 to 10 in six bands, a chip on the token, and a
// generic line in the log - what the intensity is, never what is seen.
// The GM supplies the vision; the sim supplies the number.
//
//   Hl<intensity>            STEP: the intensity falls one a round, gone at 0 (Haywire's ladder)
//   Hl<intensity>:<rounds>   HOLD: the intensity stands, the rounds tick, gone at 0 (a timed Pen:)
//
// Both live in `state.conditions` beside every other code, so the
// chip, the sync, the undo and the log come free. The countdown has
// one `Hl` branch that tells the two apart.
//
// INDUCING one is a PROFILE (`HallucinogenSpec`, on a weapon beside
// its afflictions and exposures, or the picker's ROLL): a vector the
// resistance engine judges it by, a resist test, the intensity dice,
// a bonus, a min and a max, a duration, how a second dose STACKS
// (raise / refresh / extend) and how it DECAYS (step / hold). No
// `Hallucinogenic(X)` special: presets give the one-click feel.
//
// The CODE - bands, parse, describe, the lines, the tick, the merge -
// is `hallucinationCode.ts`, a leaf `conditions.ts` and `countdown.ts`
// may import without a cycle; this module re-exports it and adds the
// INDUCER, which needs the resistance engine and the weapon context.
//
// Pure. Dice through engine/dice.
// ============================================================
export * from './hallucinationCode';
import { rollDiceExpression } from './dice';
import { effectReaches, sideKey, type EffectAffects } from './effectScope';
import {
  HALLUCINATION_MAX,
  mergeHallucination,
  narrateInduction,
  narrateStacked,
  type HallucinationStacking,
} from './hallucinationCode';
import type { AfflictionTestId } from './afflictions';
import { afflictionTriggered } from './afflictions';
import type { WeaponEffectContext, WeaponEffectResult, WeaponEffectDetail } from './weaponEffects';
import { resistTest, describeResist, type Vector } from './resistances';
import { hasWS } from './weaponSpecials';
import { immuneSourcesFor } from './conditionEligibility';

// ── Inducing ──────────────────────────────────────────────────

type HallucinationDecay = 'step' | 'hold';

export interface HallucinogenSpec {
  name: string;
  /** The channel the resistance engine judges it by - gas, toxin, psychic… */
  vector: Vector;
  trigger: 'onHit' | 'onWound' | 'onCrit';
  /** Absent/null = no resist; it simply takes. */
  resist?: { characteristic: AfflictionTestId; modifier: number } | null;
  /** Dice for the intensity - '1d10', '1d5+3'. */
  intensity: string;
  bonus: number;
  min: number;
  max: number;
  /** dof/dos (v8.367.3): the victim's failure, or the attacker's success on the hit. */
  duration: { mode: 'rounds' | 'dice' | 'permanent' | 'dof' | 'dos'; rounds?: number; dice?: string };
  /** Who it reaches, by the attacker's team (v8.367.4). Absent = anyone it hits. */
  affects?: EffectAffects;
  stacking: HallucinationStacking;
  decay: HallucinationDecay;
}

/** The default profile - a hallucinogen grenade. */
export function defaultHallucinogen(): HallucinogenSpec {
  return {
    name: 'Hallucinogen',
    vector: 'gas',
    trigger: 'onHit',
    resist: { characteristic: 'toughness', modifier: -10 },
    intensity: '1d10',
    bonus: 0,
    min: 1,
    max: 10,
    duration: { mode: 'dice', dice: '1d5' },
    stacking: 'raise',
    decay: 'step',
  };
}

export const HALLUCINOGEN_PRESETS: readonly HallucinogenSpec[] = [
  defaultHallucinogen(),
  {
    name: 'Phantasm',
    vector: 'psychic',
    trigger: 'onHit',
    resist: { characteristic: 'willpower', modifier: -10 },
    intensity: '1d5+3',
    bonus: 0,
    min: 1,
    max: 10,
    duration: { mode: 'rounds', rounds: 3 },
    stacking: 'refresh',
    decay: 'hold',
  },
];

/** Roll the profile's intensity: dice + bonus, clamped to [min, max]. One draw per die. */
export function rollHallucinationIntensity(
  profile: Pick<HallucinogenSpec, 'intensity' | 'bonus' | 'min' | 'max'>,
): number {
  const rolled = rollDiceExpression(profile.intensity || '1d10').total + (profile.bonus || 0);
  const lo = Math.max(1, Math.min(HALLUCINATION_MAX, profile.min || 1));
  const hi = Math.max(lo, Math.min(HALLUCINATION_MAX, profile.max || HALLUCINATION_MAX));
  return Math.max(lo, Math.min(hi, rolled));
}

/** The rounds a HOLD lasts for this landing (a STEP has none). */
export function hallucinationRounds(profile: HallucinogenSpec, dof: number, dos = 0): number | null {
  if (profile.decay === 'step') return null;
  switch (profile.duration.mode) {
    case 'permanent':
      return 0;
    case 'rounds':
      return Math.max(1, profile.duration.rounds ?? 1);
    case 'dof':
      return Math.max(1, dof);
    case 'dos':
      return Math.max(1, dos);
    case 'dice':
      return Math.max(1, rollDiceExpression(profile.duration.dice || '1d5').total);
  }
}

const ICON = '😵';

const RESIST_LABEL: Record<AfflictionTestId, string> = {
  toughness: 'Toughness',
  agility: 'Agility',
  strength: 'Strength',
  willpower: 'Willpower',
};

/** Log one line: the narrative note and its detail row carry the same text. */
function report(result: WeaponEffectResult, detail: WeaponEffectDetail): void {
  result.narrativeNotes.push(detail.result);
  result.effectDetails.push(detail);
}

/**
 * The victim's resist, when the profile offers one (and the weapon is not
 * Hexed): the degrees of failure the dose lands with, or null when it was
 * shrugged off. No resist lands with one.
 */
function resistHallucinogen(
  spec: HallucinogenSpec,
  ctx: WeaponEffectContext,
  result: WeaponEffectResult,
): number | null {
  if (!spec.resist || hasWS(ctx.specialRules, 'Hexed')) return 1;
  const testId = spec.resist.characteristic === 'agility' ? 'dodge' : spec.resist.characteristic;
  const out = resistTest(
    ctx.target as unknown as Record<string, unknown>,
    spec.vector,
    testId,
    0,
    spec.resist.modifier,
    {
      ignoreImmunity: hasWS(ctx.specialRules, 'Purgatus'),
    },
  );
  const described = describeResist(out, `${RESIST_LABEL[spec.resist.characteristic]} test`);
  const detail: WeaponEffectDetail = { name: spec.name, icon: ICON, success: out.passed, result: '' };
  if (out.test) {
    detail.roll = out.test.roll;
    detail.target = out.test.target;
  }
  detail.result = `${ICON} ${spec.name}: ${described}${resistEnding(out.passed, out.immune)}`;
  report(result, detail);
  return out.passed ? null : Math.max(1, out.test?.dof ?? 1);
}

/** How a resist line ends: shrugged off (immune, or passed), or a plain stop when it takes. */
function resistEnding(passed: boolean, immune: boolean): string {
  if (!passed) return '.';
  return immune ? ' - nothing takes hold.' : ' - the hallucinogen finds no purchase.';
}

/** Land one dose: roll its intensity, then its hold's rounds, and merge it into what they already see. */
function induce(spec: HallucinogenSpec, ctx: WeaponEffectContext, dof: number, result: WeaponEffectResult): void {
  const name = ctx.target.name || 'The target';
  const intensity = rollHallucinationIntensity(spec);
  const rounds = hallucinationRounds(spec, dof, ctx.dos);
  const merged = mergeHallucination(ctx.newConds, { intensity, rounds }, spec.stacking, spec.max);
  const line = merged.from === null ? narrateInduction(name, merged.to) : narrateStacked(name, merged.from, merged.to);
  report(result, { name: spec.name, icon: ICON, result: line, conditionApplied: merged.code });
}

/** True when the profile's trigger fired and it reaches this victim. */
function hallucinogenFires(spec: HallucinogenSpec, ctx: WeaponEffectContext): boolean {
  if (!afflictionTriggered({ name: spec.name, trigger: spec.trigger, effects: [] }, ctx)) return false;
  return effectReaches(
    spec.affects,
    sideKey(ctx.attackerSideTag, ctx.attackerTeamTag),
    sideKey(ctx.target.sideTag, ctx.target.teamTag),
  );
}

/**
 * A trait that refuses Hallucinating outright (From Beyond, ruling 2026-10-11): said, and no die spent - whatever
 * the vector, resisted or not. Purgatus argues with physiology and wins, as for every condition.
 */
function refusedOutright(spec: HallucinogenSpec, ctx: WeaponEffectContext, result: WeaponEffectResult): boolean {
  if (hasWS(ctx.specialRules, 'Purgatus')) return false;
  const sources = immuneSourcesFor(ctx.target as unknown as Record<string, unknown>, 'Hl');
  if (!sources.length) return false;
  report(result, {
    name: spec.name,
    icon: ICON,
    result: `${ICON} ${spec.name}: immune (${sources.join(', ')}) - nothing takes hold.`,
  });
  return true;
}

/** One profile: if it fires and is not shrugged off, the dose lands. */
function processSpec(spec: HallucinogenSpec, ctx: WeaponEffectContext, result: WeaponEffectResult): void {
  if (!hallucinogenFires(spec, ctx) || refusedOutright(spec, ctx, result)) return;
  const dof = resistHallucinogen(spec, ctx, result);
  if (dof !== null) induce(spec, ctx, dof, result);
}

/**
 * Resolve a weapon's hallucinogens against the victim - AFTER the
 * exposures, so its dice append to the seeded sequence. Per victim, so
 * a gas blast rolls every intensity separately. Per profile the dice run:
 * the resist, the intensity, then the hold's rounds.
 */
export function processHallucinogens(ctx: WeaponEffectContext): WeaponEffectResult {
  const result: WeaponEffectResult = { narrativeNotes: [], extraDamage: 0, effectDetails: [] };
  const specs = (ctx.slot as { hallucinogens?: HallucinogenSpec[] }).hallucinogens;
  if (!specs?.length || ctx.targetKilled) return result;
  for (const spec of specs) processSpec(spec, ctx, result);
  return result;
}
